import { ChangeDetectorRef, Component, inject, Input, OnChanges, OnInit, SimpleChanges, HostBinding, PLATFORM_ID, ViewChild, ElementRef } from '@angular/core';
import { Amenity, Property, PropertyAmenity } from '../../models/property.model';
import { ActivatedRoute, Router } from '@angular/router';
import { PropertyService } from '../../services/property.service';
import { PropertyLikeService } from '../../services/property-like.service';
import { switchMap, tap } from 'rxjs';
import { MatSpinner } from '@angular/material/progress-spinner';
import { PropertyCard } from '../property-card/property-card';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { sortImagesIfNeeded } from '../../services/property-image.service';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { trigger, transition, style, animate } from '@angular/animations';
import { Location } from '@angular/common';
import { DomSanitizer, Meta, SafeStyle, Title } from '@angular/platform-browser';
import { LoaderComponent } from '../../../../shared/general/components/loader/loader.component';
import { environment } from '../../../../../environments/environment';
import { SeoService } from '../../../../shared/services/seo.service';
import { GtmService } from '../../../../core/services/gtm.service';
import { AuthService } from '../../../../core/services/auth.service';

@Component({
  selector: 'app-property-view',
  imports: [
    CommonModule,
    MatSpinner,
    PropertyCard,
    MatBottomSheetModule,
    LoaderComponent
  ],
  animations: [
    trigger('sheetAnimation', [
      transition(':enter', [
        style({ transform: 'translateY(100%)' }),
        animate('400ms ease-out', style({ transform: 'translateY(0)' }))
      ])
    ])
  ],
  host: { '[@sheetAnimation]': '' },
  standalone: true,
  templateUrl: './property-view.html',
  styleUrl: './property-view.scss',
})
export class PropertyView implements OnChanges, OnInit {
  private propService = inject(PropertyService);
  private likeService = inject(PropertyLikeService);
  private cdr = inject(ChangeDetectorRef);

  private meta = inject(Meta);
  private title = inject(Title);
  seo = inject(SeoService);
  gtm = inject(GtmService);
  platformId = inject(PLATFORM_ID);
  @Input() set id(propertyId: string) {
    if (propertyId) {
      // 1. Flush old data immediately
      this.property = null;
      this.loading = true;
      this.error = null;
      
      // 2. Force a render cycle to clear children
      this.cdr.detectChanges(); 
      
      // 3. Load the new one
      this.loadProperty(propertyId);
    }
  }
  private auth = inject(AuthService);
  private router = inject(Router);
  private location = inject(Location);
  private route = inject(ActivatedRoute);
  private sanitizer = inject(DomSanitizer);
  private yOffset = 0;

  @HostBinding('style.transform')
  get transform(): SafeStyle {
    return this.sanitizer.bypassSecurityTrustStyle(`translateY(${this.yOffset}px)`);
  }

  onPanStart(event: any) {
    // Prevent default scrolling behavior while grabbing the bar
    if (isPlatformBrowser(this.platformId)) {
      event.preventDefault();
    }
  }

  onPanMove(event: any) {
    // event.deltaY is the distance moved from the start point
    // We only care about pulling DOWN (positive deltaY)
    if (event.deltaY > 0) {
      this.yOffset = event.deltaY;
      this.cdr.detectChanges(); // Required for OnPush / SSR hydration
    }
  }

  onPanEnd(event: any) {
    // If pulled down more than 150px OR flicked fast (high velocity)
    if (this.yOffset > 150 || event.velocityY > 0.3) {
      this.close();
    } else {
      // Snap back to top if they didn't pull far enough
      this.yOffset = 0;
      this.cdr.detectChanges();
    }
  }
  
  @Input() inputProperty: Property | undefined;
  @Input() data: any;
  @ViewChild('sheetContainer') sheetContainer!: ElementRef;
  property: Property | null = null;
  loading = true;
  error: string | null = null;
  amenities: Amenity[] = [];
  
  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {}
    const resolvedData = this.route.snapshot.data['property'];
    
    if (resolvedData) {
     // console.log('Resolved Property found for SSR:', resolvedData);
      this.property = resolvedData;
      this.loading = false;
      
      // 2. Set tags synchronously so the server sees them
      this.updateTags(resolvedData);
      
      if (isPlatformBrowser(this.platformId)) {
        this.trackPageView();
      }

      // 3. Trigger secondary data loads
      this.getPropertyAmenities(resolvedData.id);
      this.loadPropertyCalendars(resolvedData.id);
      
      // 4. Critical for Zoneless SSR to pick up the changes
      this.cdr.detectChanges();
    }
  }

  // private async initHammer() {
  //   if (isPlatformBrowser(this.platformId)) {
  //     // Dynamically load HammerJS only in the browser
  //     const Hammer = (await import('hammerjs')).default; 
      
  //     if (this.sheetContainer?.nativeElement) {
  //       const mc = new Hammer(this.sheetContainer.nativeElement);
        
  //       // Hammer needs to know which directions to allow
  //       mc.get('swipe').set({ direction: Hammer.DIRECTION_HORIZONTAL });

  //       mc.on("panstart panmove panend", (ev: any) => {
  //         console.log(`${ev.type} gesture detected!`);
  //         // Trigger manual change detection if this updates a variable
          
  //         this.cdr.detectChanges();
  //       });
  //     }
  //   }
  // }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['inputProperty'] && changes['inputProperty'].currentValue) {
      //console.log('🔄 Property changed:', this.property);
      this.property = changes['inputProperty'].currentValue;
      if (this.property && this.property.id) {
        this.getPropertyAmenities(this.property?.id);
        this.loadPropertyCalendars(this.property?.id);
        this.loadPropertyWithLikeStatus(this.property?.id);
        if (Array.isArray(this.property.images) && this.property.images.length){
          this.property.images = sortImagesIfNeeded(this.property.images);
        }
      }
    }
  }

  loadPropertyWithLikeStatus(id: string) {
    this.likeService.getLoggedInUserLikes().pipe(
      tap((userLikes) => {
        const isLiked = userLikes.some((like) => like.propertyId === id);
        if (this.property) {
          this.property.liked = isLiked;
        }
      })
    ).subscribe();
  }

  getPropertyAmenities = (propertyId: string) => {
    //console.log('getPropertyAmenities called with propertyId:', propertyId);
    this.loading = true;

    this.propService.getAmenities().pipe(
      switchMap((amenities: Amenity[]) => {
        this.amenities = amenities;
       // console.log('Loaded amenities:', this.amenities);
        return this.propService.getPropertyAmenities(propertyId);
      })
    ).subscribe({
      next: (propertyAmenities: PropertyAmenity[]) => {
        // console.log('Received propertyAmenities:', propertyAmenities);
        // console.log('Current this.amenities:', this.amenities);
        // console.log('Current this.property:', this.property);

        if (this.property === null || this.property === undefined) {
          console.error('Property is null or undefined, cannot assign amenities');
          this.error = 'Property not found';
          this.loading = false;
          this.cdr.detectChanges();
          return;
        }

        if (!this.amenities || this.amenities.length === 0) {
          console.error('this.amenities is empty or undefined');
          this.error = 'No amenities available to filter';
          this.property.amenities = [];
          this.loading = false;
          this.cdr.detectChanges();
          return;
        }

        if (!propertyAmenities || propertyAmenities.length === 0) {
          console.warn(`No property amenities found for propertyId: ${propertyId}`);
          this.property.amenities = []; // Safe fallback
          // We don't set an error here because a property having 0 amenities is a valid state
          this.loading = false;
          this.cdr.detectChanges();
          return;
        }

        const propertyAmenityIds = new Set(propertyAmenities.map((pa) => pa.amenityId));
        //console.log('propertyAmenityIds Set:', Array.from(propertyAmenityIds));

        this.property.amenities = this.amenities.filter((amenity) => {
          const isMatch = propertyAmenityIds.has(amenity.id);
          return isMatch;
        });

        //console.log('Filtered this.property.amenities:', this.property.amenities);

        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('API Error:', error);
        this.error = 'Failed to load amenities or property amenities';
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  };

  private loadPropertyCalendars(propertyId: string): void {
    //console.log('getPropertyCalendars for ID:', propertyId);
    this.loading = true;

    this.propService.getPropertyCalendars(propertyId).subscribe({
      next: (calendars) => {
        //console.log('Calendars loaded:', calendars);

        if (!this.property) {
          //console.error('Property is null — cannot assign calendars');
          this.error = 'Property data missing';
          this.loading = false;
          this.cdr.markForCheck();
        this.cdr.detectChanges();
          return;
        }

        // Assign calendars to property
        this.property.calendars = calendars;
        this.loading = false;
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Failed to load calendars:', error);
        this.error = 'Failed to load calendar data';
        this.loading = false;
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      }
    });
  }

  loadProperty = (id: string) => {
    //console.log('load property');
    if (!id) {
      this.error = 'No property ID found';
      this.loading = false;
      return;
    }

    this.propService.getPropertyById(id).subscribe({
      next: (property) => {
        this.property = property;
        this.loading = false;
        //console.log('Property loaded:', property);

        // Load calendars after property is set
        this.loadPropertyCalendars(property.id);
        if (Array.isArray(this.property.images) && this.property.images.length){
          this.property.images = sortImagesIfNeeded(this.property.images);
        }
        this.updateTags();
        this.trackPageView();
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.error = 'Property not found';
        this.loading = false;
        console.error('API Error:', error);
        this.cdr.detectChanges();
      }
    });
  }

  close() {
    //console.log('close property view');
    const state = window.history.state;

    if (state && state.navigationId > 1) {
      // The user has navigated within the app, so we go back
      this.location.back();
    } else {
      // No history (likely a direct link or refresh), go to the main list
      this.router.navigate(['/property'], { 
        queryParams: { state: this.property?.address.state } 
      });
    }
  }

  private updateTags(p?: Property) {
    const target = p || this.property;
    if (!target) return;

    const pageTitle = `${target.displayName ?? target.propertyType} in ${target.address.city}, ${target.address.state}`;
    this.title.setTitle(`${pageTitle} | Horizinns`);
    
    const mainImg = target.images?.find(img => img.sortOrder === 0) ?? target.images?.[0];
    const formattedCost = new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
    }).format(target.cost ?? 0);
    
    const metaDescription = `${target.bedrooms} bed | ${target.bathrooms} bath starting at ${formattedCost}`;

    // Update Meta Tags using the property target
    this.meta.updateTag({ name: 'description', content: metaDescription });
    
    // Explicitly use 'property' to overwrite index.html hardcoded tags
    this.meta.updateTag({ property: 'og:title', content: `${pageTitle} | Horizinns` });
    this.meta.updateTag({ property: 'og:description', content: metaDescription });
    this.meta.updateTag({ name: 'twitter:title', content: `${pageTitle} | Horizinns` });
    this.meta.updateTag({ name: 'twitter:description', content: metaDescription });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });

    if (mainImg?.imageUrl) {
      this.meta.updateTag({ property: 'og:image', content: mainImg.imageUrl });
      this.meta.updateTag({ name: 'twitter:image', content: mainImg.imageUrl });
    }
    
    this.meta.updateTag({ property: 'og:url', content: `${environment.appUrl}/property/${target.id}` });
    this.meta.updateTag({ name: 'twitter:url', content: `${environment.appUrl}/property/${target.id}` });

    this.seo.updateMetaTags(pageTitle, metaDescription, mainImg?.imageUrl, `${environment.appUrl}/property/${target.id}`);
    this.cdr.detectChanges();
  }

  trackPageView() {
    const pageTitle = `${this.property?.displayName ?? this.property?.propertyType} in ${this.property?.address.city}, ${this.property?.address.state}`
    this.gtm.pushEvent('view_property', {
      'property_id': this.property?.id ?? '',
      'property_name': pageTitle,
      'user_id': this.auth.userId ?? 'anonymous'
    });
  }
}
